import { movieGenres, movieRegions, movieTypes, movieYears } from '@/mocks/movies';
import type { SortKey } from '@/lib/movies';

export interface Filters {
  q: string;
  type: string;
  genre: string;
  region: string;
  year: string;
  sort: SortKey;
  page: number;
  loadMode: 'page' | 'more';
}

export const defaultFilters: Filters = {
  q: '',
  type: '全部',
  genre: '全部',
  region: '全部',
  year: '全部',
  sort: 'updated',
  page: 1,
  loadMode: 'page',
};

interface FilterBarProps {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
}

const selectClass =
  'rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer';

export default function FilterBar({ filters, onChange, onReset }: FilterBarProps) {
  const typeTabs = ['全部', ...movieTypes];

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-12 shrink-0 text-sm text-foreground-500">类型</span>
        <div className="flex flex-wrap gap-2">
          {typeTabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onChange({ type: t, page: 1 })}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filters.type === t
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
        <span className="w-12 shrink-0 text-sm text-foreground-500">题材</span>
        <div className="flex flex-1 flex-wrap gap-2">
          {['全部', ...movieGenres].map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => onChange({ genre: g, page: 1 })}
              className={`rounded-md px-3 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                filters.genre === g
                  ? 'bg-accent-500 text-background-50'
                  : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-background-200 pt-4">
        <select
          value={filters.region}
          onChange={(e) => onChange({ region: e.target.value, page: 1 })}
          className={selectClass}
          aria-label="选择地区"
        >
          <option value="全部">全部地区</option>
          {movieRegions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        <select
          value={filters.year}
          onChange={(e) => onChange({ year: e.target.value, page: 1 })}
          className={selectClass}
          aria-label="选择年份"
        >
          <option value="全部">全部年份</option>
          {movieYears.map((y) => (
            <option key={y} value={String(y)}>
              {y} 年
            </option>
          ))}
        </select>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">排序</span>
          <button
            type="button"
            onClick={() => onChange({ sort: 'updated', page: 1 })}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
              filters.sort === 'updated' ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700'
            }`}
          >
            最近更新
          </button>
          <button
            type="button"
            onClick={() => onChange({ sort: 'hot', page: 1 })}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
              filters.sort === 'hot' ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700'
            }`}
          >
            最热门
          </button>
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            重置
          </button>
        </div>
      </div>
    </div>
  );
}